'use client';

import { Form } from 'informed';
import { usePathname, useSearchParams } from 'next/navigation';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { NewPassword, recoveryPassword } from '@/api/auth';
import { Button } from '@/components/buttons';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Password } from '@/components/form/Password';
import { showTooltip } from '@/helpers/tooltip';
import { translationFactory } from '@/helpers/transl';
import { validatePassword } from '@/helpers/validators';
import { useNavigation } from '@/hooks/navigation';
import { useSwrMutation } from '@/hooks/swr';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScLoginWrapButtons } from '@/components/dialogs/Login/styled';
import { ScContainer, ScFormBlock } from '@/styles/common';

export const EditPassword: FC = () => {
  const { navigate } = useNavigation();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const resetPasswordToken = searchParams.get('reset_password_token');

  useEffect(() => {
    if (pathname.includes('/users/password/edit') && !resetPasswordToken) {
      navigate('/', true);
    }
  }, [resetPasswordToken, pathname, navigate]);

  const [error, setError] = useState<string>();

  const { t } = useTranslation('common');

  const { trigger: requestRecoveryPassword, isMutating } = useSwrMutation({
    key: 'recoveryPassword',
    fetcher: recoveryPassword,
  });

  const handleSubmit = useCallback(
    async (values: NewPassword) => {
      if (values.password !== values.passwordConfirmation) {
        setError('common:auth.recovery.passwordsNotMatch');

        return;
      }

      const response = await requestRecoveryPassword({
        ...values,
        resetPasswordToken,
      });

      if (!response) {
        showTooltip({
          type: TooltipTypes.ERROR,
          translate: true,
          body: translationFactory('common:auth.recovery.error'),
        });

        navigate('/', true);

        return;
      }

      showTooltip({
        type: TooltipTypes.SUCCESS,
        translate: true,
        body: translationFactory('common:auth.recovery.success'),
      });

      navigate('/', true);
    },
    [resetPasswordToken, requestRecoveryPassword, navigate]
  );

  return (
    <ScContainer>
      <ScFormBlock>
        <h1>{t('auth.recovery.changeYourPassword')}</h1>
        <Form<NewPassword> onSubmit={({ values }) => handleSubmit(values)}>
          <FormGroup title={t('auth.recovery.newPassword')}>
            <Password
              name="password"
              validate={validatePassword}
              validateOn="change"
              strengthChecker
            />
          </FormGroup>
          <FormGroup title={t('auth.recovery.confirmPassword')}>
            <Password
              name="passwordConfirmation"
              error={error && t(error)}
              validateOn="blur"
            />
          </FormGroup>

          <ScLoginWrapButtons>
            <Button type="submit" primary large wide isLoading={isMutating}>
              {t('auth.recovery.changeMyPassword')}
            </Button>
          </ScLoginWrapButtons>
        </Form>
      </ScFormBlock>
    </ScContainer>
  );
};
